/**
 * Maps Verifast government_id_checks / selfie_checks into UI checklist rows.
 */

export type VerifastCheckRequirement = "required" | "not_required" | string;

export type VerifastCheckDefinition = {
  status?: boolean | null;
  reasons?: string[];
  requirement?: VerifastCheckRequirement;
};

export type VerifastIdentityDetails = {
  id_name?: string | null;
  phone?: string | null;
  email?: string | null;
  date_of_birth?: string | null;
  address?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
  face_match?: boolean | null;
  name_match_id?: boolean | null;
};

export type VerifastChecksSnapshot = {
  government_id_checks?: Record<string, VerifastCheckDefinition> | null;
  selfie_checks?: Record<string, VerifastCheckDefinition> | null;
  document_type?: string | null;
  identity_details?: VerifastIdentityDetails | null;
};

export type VerifastChecklistItem = {
  key: string;
  label: string;
  passed: boolean;
  required: boolean;
  optional: boolean;
};

export type VerifastChecklistSummaryVariant = "complete" | "warning" | "failed";

export type VerifastChecklistSection = {
  title: string;
  allRequiredPassed: boolean;
  summaryVariant: VerifastChecklistSummaryVariant;
  summaryMessage: string;
  /** Failed mandatory checks — shown in collapsed preview (Verifast dashboard parity). */
  failedRequiredPreview: VerifastChecklistItem[];
  items: VerifastChecklistItem[];
};

export function computeChecklistSummary(items: VerifastChecklistItem[]): {
  summaryVariant: VerifastChecklistSummaryVariant;
  summaryMessage: string;
  failedRequiredPreview: VerifastChecklistItem[];
  allRequiredPassed: boolean;
} {
  const required = items.filter((item) => item.required);
  const optional = items.filter((item) => item.optional);
  const failedRequired = required.filter((item) => !item.passed);
  const failedOptional = optional.filter((item) => !item.passed);

  if (failedRequired.length > 0) {
    return {
      summaryVariant: "failed",
      summaryMessage: "Some Required Checks Did Not Pass",
      failedRequiredPreview: failedRequired,
      allRequiredPassed: false,
    };
  }

  if (failedOptional.length > 0) {
    return {
      summaryVariant: "warning",
      summaryMessage: "All Required Checks Passed",
      failedRequiredPreview: [],
      allRequiredPassed: true,
    };
  }

  return {
    summaryVariant: "complete",
    summaryMessage: "All Required Checks Passed",
    failedRequiredPreview: [],
    allRequiredPassed: required.length === 0 ? true : required.every((item) => item.passed),
  };
}

export type VerifastDetailField = {
  key: string;
  label: string;
  value: string;
  booleanValue?: boolean | null;
};

export type VerifastDetailSection = {
  title: string;
  columns: {
    title: string;
    fields: VerifastDetailField[];
  }[];
};

export type VerifastModalSection =
  | ({ type: "checks" } & VerifastChecklistSection)
  | ({ type: "details" } & VerifastDetailSection);

const CHECK_LABEL_OVERRIDES: Record<string, string> = {
  id_aamva_database_lookup: "AAMVA database lookup",
  id_account_comparison: "Account comparison",
  id_age_comparison: "Age comparison",
  id_attribute_comparison: "Attribute comparison",
  id_barcode_detection: "Barcode",
  id_barcode_inconsistency_detection: "Barcode inconsistency",
  id_blur_detection: "Blur",
  id_color_inconsistency_detection: "Color",
  id_compromised_detection: "Compromised submission",
  id_damaged_detection: "Damaged detection",
  id_disallowed_country_detection: "Allowed country",
  id_disallowed_type_detection: "Allowed ID type",
  id_double_side_detection: "Double side",
  id_electronic_replica_detection: "Electronic replica",
  id_entity_detection: "Government ID",
  id_expired_detection: "Expiration",
  id_extracted_properties_detection: "Extracted properties",
  id_extraction_inconsistency_detection: "Extraction inconsistency",
  id_fabrication_detection: "Fabrication",
  id_glare_detection: "Glare",
  id_handwriting_detection: "Handwriting",
  id_inconsistent_repeat_detection: "Inconsistent repeat",
  id_inquiry_comparison: "Inquiry comparison",
  id_mrz_detection: "MRZ",
  id_mrz_inconsistency_detection: "MRZ inconsistency",
  id_portrait_clarity_detection: "Portrait clarity",
  id_portrait_detection: "Portrait",
  id_public_figure_detection: "Public figure",
  id_repeat_detection: "Repeat detection",
  id_selfie_comparison: "Selfie-to-ID comparison",
  id_tamper_detection: "Tamper detection",
  selfie_attribute_comparison: "Attribute comparison",
  selfie_id_comparison: "Selfie-to-ID comparison",
  selfie_pose_detection: "Pose position",
  selfie_pose_repeat_detection: "Pose repeat",
  selfie_multiple_faces_detection: "Multiple faces",
  selfie_account_comparison: "Account comparison",
  selfie_suspicious_entity_detection: "Suspicious entity",
  selfie_liveness_detection: "Selfie liveness",
  selfie_glasses_detection: "Glasses",
  selfie_glare_detection: "Glare",
  selfie_experimental_model_detection: "Experimental",
  selfie_portrait_quality_detection: "Portrait quality",
  selfie_public_figure_detection: "Public figure",
  selfie_age_comparison: "Age comparison",
  selfie_face_covering_detection: "Face covering",
  selfie_video_quality_detection: "Video quality",
  selfie_image_quality_detection: "Image quality",
};

function formatCheckLabel(key: string): string {
  const override = CHECK_LABEL_OVERRIDES[key];
  if (override) return override;
  const stripped = key.replace(/^(id_|selfie_)/, "").replace(/_detection$/, "");
  return stripped
    .split("_")
    .filter(Boolean)
    .map((part, index) => {
      const lower = part.toLowerCase();
      if (index > 0 && ["and", "or", "of", "to", "in"].includes(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

function isOptionalRequirement(requirement?: VerifastCheckRequirement): boolean {
  return String(requirement || "").toLowerCase() === "not_required";
}

function isRequiredRequirement(requirement?: VerifastCheckRequirement): boolean {
  return String(requirement || "").toLowerCase() === "required";
}

function checkPassed(definition: VerifastCheckDefinition): boolean {
  if (definition.status === true) return true;
  if (definition.status === false) return false;
  return isOptionalRequirement(definition.requirement);
}

export function buildChecklistSection(
  title: string,
  checks: Record<string, VerifastCheckDefinition> | null | undefined,
): VerifastChecklistSection | null {
  if (!checks || typeof checks !== "object") return null;

  const items = Object.entries(checks).map(([key, definition]) => {
    const optional = isOptionalRequirement(definition?.requirement);
    const required = isRequiredRequirement(definition?.requirement);
    return {
      key,
      label: formatCheckLabel(key),
      passed: checkPassed(definition || {}),
      required,
      optional,
    };
  });

  if (!items.length) return null;

  const summary = computeChecklistSummary(items);

  return {
    title,
    allRequiredPassed: summary.allRequiredPassed,
    summaryVariant: summary.summaryVariant,
    summaryMessage: summary.summaryMessage,
    failedRequiredPreview: summary.failedRequiredPreview,
    items,
  };
}

function formatDetailDate(value: unknown): string {
  if (value == null || value === "") return "N/A";
  const text = String(value).trim();
  if (!text) return "N/A";
  const parsed = Date.parse(text);
  if (Number.isFinite(parsed)) {
    try {
      const date = new Date(parsed);
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())} ${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date.getFullYear()}`;
    } catch {
      return text;
    }
  }
  return text;
}

function formatDetailText(value: unknown): string {
  if (value == null) return "N/A";
  const text = String(value).trim();
  return text || "N/A";
}

export function parseVerifastIdentityDetails(raw: unknown): VerifastIdentityDetails | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const hasAnyField = [
    "id_name",
    "phone",
    "email",
    "date_of_birth",
    "address",
    "created_at",
    "completed_at",
    "face_match",
    "name_match_id",
  ].some((key) => row[key] != null && row[key] !== "");
  if (!hasAnyField) return null;

  return {
    id_name: row.id_name != null ? String(row.id_name) : null,
    phone: row.phone != null ? String(row.phone) : null,
    email: row.email != null ? String(row.email) : null,
    date_of_birth: row.date_of_birth != null ? String(row.date_of_birth) : null,
    address: row.address != null ? String(row.address) : null,
    created_at: row.created_at != null ? String(row.created_at) : null,
    completed_at: row.completed_at != null ? String(row.completed_at) : null,
    face_match: typeof row.face_match === "boolean" ? row.face_match : null,
    name_match_id:
      typeof row.name_match_id === "boolean" ? row.name_match_id : null,
  };
}

export function parseVerifastChecksSnapshot(raw: unknown): VerifastChecksSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const gov = row.government_id_checks;
  const selfie = row.selfie_checks;
  const identityDetails = parseVerifastIdentityDetails(row.identity_details);
  if (
    (!gov || typeof gov !== "object") &&
    (!selfie || typeof selfie !== "object") &&
    !identityDetails
  ) {
    return null;
  }
  return {
    government_id_checks:
      gov && typeof gov === "object"
        ? (gov as Record<string, VerifastCheckDefinition>)
        : null,
    selfie_checks:
      selfie && typeof selfie === "object"
        ? (selfie as Record<string, VerifastCheckDefinition>)
        : null,
    document_type:
      row.document_type != null ? String(row.document_type) : null,
    identity_details: identityDetails,
  };
}

export function mergeVerifastChecksSnapshot(
  checks: VerifastChecksSnapshot | null | undefined,
  identityDetails: VerifastIdentityDetails | null | undefined,
): VerifastChecksSnapshot | null {
  if (!checks && !identityDetails) return null;
  if (!checks) {
    return identityDetails ? { identity_details: identityDetails } : null;
  }
  return {
    ...checks,
    identity_details: identityDetails ?? checks.identity_details ?? null,
  };
}

export function buildVerifastChecklistSections(
  snapshot: VerifastChecksSnapshot | null | undefined,
): VerifastChecklistSection[] {
  if (!snapshot) return [];
  const sections: VerifastChecklistSection[] = [];
  const idSection = buildChecklistSection(
    "ID Checks",
    snapshot.government_id_checks,
  );
  const selfieSection = buildChecklistSection(
    "Selfie Checks",
    snapshot.selfie_checks,
  );
  if (idSection) sections.push(idSection);
  if (selfieSection) sections.push(selfieSection);
  return sections;
}

export function buildVerifastIdentityDetailSection(
  details: VerifastIdentityDetails | null | undefined,
): VerifastDetailSection | null {
  if (!details) return null;

  const identificationFields: VerifastDetailField[] = [
    { key: "id_name", label: "ID Name", value: formatDetailText(details.id_name) },
    { key: "phone", label: "Phone", value: formatDetailText(details.phone) },
    { key: "email", label: "Email", value: formatDetailText(details.email) },
    {
      key: "date_of_birth",
      label: "Date of birth",
      value: formatDetailText(details.date_of_birth),
    },
    { key: "address", label: "Address", value: formatDetailText(details.address) },
  ];

  const verificationFields: VerifastDetailField[] = [
    {
      key: "created_at",
      label: "Created At",
      value: formatDetailDate(details.created_at),
    },
    {
      key: "completed_at",
      label: "Completed At",
      value: formatDetailDate(details.completed_at),
    },
    {
      key: "face_match",
      label: "Face Match",
      value: "",
      booleanValue: details.face_match,
    },
    {
      key: "name_match_id",
      label: "Name/ID Match",
      value: "",
      booleanValue: details.name_match_id,
    },
  ];

  return {
    title: "Identification & Verifications",
    columns: [
      { title: "Identification Details", fields: identificationFields },
      { title: "Verifications", fields: verificationFields },
    ],
  };
}

export function buildVerifastModalSections(
  snapshot: VerifastChecksSnapshot | null | undefined,
): VerifastModalSection[] {
  if (!snapshot) return [];
  const sections: VerifastModalSection[] = buildVerifastChecklistSections(snapshot).map(
    (section) => ({ type: "checks", ...section }),
  );
  const identitySection = buildVerifastIdentityDetailSection(snapshot.identity_details);
  if (identitySection) {
    sections.push({ type: "details", ...identitySection });
  }
  return sections;
}

export function hasVerifastChecks(snapshot: VerifastChecksSnapshot | null | undefined): boolean {
  return buildVerifastChecklistSections(snapshot).length > 0;
}

export function hasVerifastModalContent(
  snapshot: VerifastChecksSnapshot | null | undefined,
): boolean {
  return buildVerifastModalSections(snapshot).length > 0;
}
